// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnDefinitions } from './hooks/useColumnDefinitions';
import { useGetCLASettings } from './hooks/useGetCLASettings';

//import components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import WithInnerLayout from '../../../shared/CRUD/components/InnerPage/WithInnerLayout';
import useNavigateAfterCreate from '../../../shared/CRUD/components/InnerPage/hooks/useNavigateAfterCreate';
import CLAForm from './CLAForm';
import EditCLA from './EditCLA';

// import types
import type { TableCRUDLayoutHandlers } from 'components/shared/CRUD/TableCRUDLayout';
import type { CLAItem } from './CLASettings.types';

const CLASettings = ({ isInnerLayoutOpen, setIsInnerLayoutOpen }) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'adminPortal.cla' });

  const [rowData, setRowData] = useState<CLAItem>();
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const { data: claSettings, isFetching } = useGetCLASettings();
  const columnDefinitions = useColumnDefinitions();

  const toggleInnerLayout = () => {
    setIsInnerLayoutOpen(!isInnerLayoutOpen);
  };

  const handleCopyClick = (id, row, props: TableCRUDLayoutHandlers) => {
    setIsCopied(true);
    if (props.handleEditClick) {
      props.handleEditClick(id, row);
    }
  };

  const onViewClick = (rowData) => {
    setRowData(rowData);
    setIsInnerLayoutOpen(true);
  };

  useNavigateAfterCreate(claSettings, onViewClick);

  if (isInnerLayoutOpen && rowData && claSettings) {
    return (
      <EditCLA
        toggleInnerLayout={toggleInnerLayout}
        rows={claSettings}
        rowData={rowData}
      />
    );
  }

  return (
    <TableCRUDLayout
      FormComponent={(props) => <CLAForm {...props} isCopied={isCopied} />}
      rows={claSettings}
      loading={isFetching}
      columns={columnDefinitions}
      getRowId={(row) => row.claYearId}
      editable={false}
      confirmationTitle={t('deleteDialog.title')}
      confirmationBody={t('deleteDialog.body')}
      onViewClick={(id, row) => onViewClick(row)}
      customActions={[
        {
          tooltipTitle: t('copy'),
          icon: 'copy',
          onClick: handleCopyClick
        }
      ]}
    />
  );
};

const CLASettingsWithLayout = WithInnerLayout(CLASettings);
export default CLASettingsWithLayout;
